import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  tableCellClasses,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import CreateDepartmentModal from "src/components/department/CreateDepartmentModal";
import EditDepartmentModal from "src/components/department/EditDepartmentModal";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteDepartment,
  Department,
  fetchDepartments,
} from "src/features/departmentsSlice";
import { AppDispatch, RootState } from "src/store";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import LoadingSpinner from "src/components/shared/LoadingSpinner";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmDialog from "src/components/shared/ConfirmDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.grey[100],
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const DepartmentsPage = () => {
  const { t } = useTranslation();
  const { departments, loading } = useSelector(
    (state: RootState) => state.departments
  );
  const { token, user } = useSelector((state: RootState) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const companyId =
    user?.accountType === "company"
      ? (user.companyId || user.id || user.userId || "")
      : "";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [departmentToEdit, setDepartmentToEdit] =
    useState<Department | null>(null);

  const [selectedDepartment, setSelectedDepartment] =
    useState<Department | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleOpenDeleteDialog = (department: Department) => {
    setSelectedDepartment(department);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedDepartment(null);
    setIsDeleteDialogOpen(false);
  };

  const handleOpenEditModal = (department: Department) => {
    setDepartmentToEdit(department);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setDepartmentToEdit(null);
    setIsEditModalOpen(false);
  };

  const handleConfirmDelete = () => {
    if (selectedDepartment) {
      dispatch(deleteDepartment(selectedDepartment.id));
      handleCloseDeleteDialog();
    }
  };

  const handleRefresh = () => {
    if (!token || !companyId) return;
    dispatch(fetchDepartments());
  };

  const handleOpenModal = () => setIsModalOpen(true);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Fetch departments once on mount
  useEffect(() => {
    if (!token || !companyId) return;
    dispatch(fetchDepartments());
  }, [dispatch, token, companyId]);

  // Handle query params separately
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get("create-department") === "true") {
      setIsModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box
      sx={{
        p: 3,
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          width: "100%",
        }}
      >
        <ConfirmDialog
          open={isDeleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleConfirmDelete}
          question={t("departments.deleteConfirm", { name: selectedDepartment?.name })}
        />
        <Typography variant="h5" sx={{ pl: 3 }}>
          {t("departments.title")}
        </Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
          >
            {t("departments.create.title")}
          </Button>
          <IconButton sx={{ ml: 1 }}>
            <FilterListIcon />
          </IconButton>
          <IconButton sx={{ ml: 1 }} onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {departments.length ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell sortDirection="asc">
                  {t("departments.table.name")}
                </StyledTableCell>
                <StyledTableCell>{t("departments.table.industry")}</StyledTableCell>
                <StyledTableCell>{t("departments.table.location")}</StyledTableCell>
                <StyledTableCell>{t("departments.table.team")}</StyledTableCell>
                <StyledTableCell>{t("departments.table.positions")}</StyledTableCell>
                <StyledTableCell></StyledTableCell>
                <StyledTableCell></StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {departments.map((department) => (
                <StyledTableRow key={department.id}>
                  <StyledTableCell sortDirection="asc">
                    {department.name}
                  </StyledTableCell>
                  <StyledTableCell>{department.industry || "-"}</StyledTableCell>
                  <StyledTableCell>{department.location || "-"}</StyledTableCell>
                  <StyledTableCell>{department.team || "-"}</StyledTableCell>
                  <StyledTableCell>
                    <Button
                      startIcon={<VisibilityIcon />}
                      onClick={() =>
                        navigate(`/positions?departmentId=${department.id}`)
                      }
                    >
                      {t("departments.actions.view")}
                    </Button>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Button
                      startIcon={<EditIcon />}
                      onClick={() => handleOpenEditModal(department)}
                    >
                      {t("departments.actions.edit")}
                    </Button>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Button
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleOpenDeleteDialog(department)}
                    >
                      {t("departments.actions.delete")}
                    </Button>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            mt: 15,
            p: 3,
            maxWidth: 850,
          }}
        >
          <AccountBalanceIcon sx={{ fontSize: 120 }} />
          <Typography variant="h6" gutterBottom>
            {t("departments.noDepartments")}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {t("departments.description")}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
            onClick={handleOpenModal}
          >
            {t("departments.create.title")}
          </Button>
        </Box>
      )}
      <CreateDepartmentModal open={isModalOpen} onClose={handleCloseModal} />
      <EditDepartmentModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        department={departmentToEdit}
      />
    </Box>
  );
};

export default DepartmentsPage;
